import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, Download, FileText, Loader2, Pencil, Sparkles, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { StatutsDiffView } from "@/components/legal/StatutsDiffView";

const downloadFile = async (vid, fmt, version) => {
  try {
    const r = await api.get(`/legal/statuts/versions/${vid}/file?fmt=${fmt}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = `statuts_v${version}.${fmt}`; a.click();
  } catch (e) { toast.error(apiError(e)); }
};

const Changes = ({ changes }) => (
  <div className="space-y-1.5" data-testid="statuts-changes">
    {(changes || []).length === 0 && <p className="text-sm text-slate-500">L'IA n'a pas listé de changements — consulte la comparaison.</p>}
    {(changes || []).map((c, i) => (
      <div key={i} className="p-2.5 rounded border bg-white text-sm" data-testid={`statuts-change-${i}`}>
        <div className="flex items-center gap-2 flex-wrap"><b>{c.article}</b>
          <Badge variant="outline" className="text-[10px]">{c.nature === "ajoute" ? "Ajouté" : c.nature === "supprime" ? "Supprimé" : "Modifié"}</Badge>
          {c.motif && <span className="text-xs text-slate-500">{c.motif}</span>}</div>
        {c.avant && <p className="text-xs text-red-700 mt-1"><span className="text-slate-400">Avant : </span>{c.avant}</p>}
        {c.apres && <p className="text-xs text-emerald-700"><span className="text-slate-400">Après : </span>{c.apres}</p>}
      </div>
    ))}
  </div>
);

const Editor = ({ v, onSaved }) => {
  const [text, setText] = useState(v.text_markdown || "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const r = await api.patch(`/legal/statuts/versions/${v.id}`, { text_markdown: text }); toast.success("Brouillon enregistré"); onSaved(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2">
      <Textarea rows={18} className="font-mono text-xs" value={text} onChange={(e) => setText(e.target.value)} data-testid="statuts-editor" />
      <div className="flex justify-end"><Button size="sm" onClick={save} disabled={busy} data-testid="statuts-save-btn">{busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Pencil className="w-4 h-4 mr-1" />}Enregistrer et recomparer</Button></div>
    </div>
  );
};

const NeedStatuts = ({ onUploaded }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { await api.post("/legal/documents/upload?category=statuts&tags=statuts%20actuels", fd, { headers: { "Content-Type": "multipart/form-data" } }); onUploaded(); }
    catch (e) { toast.error(apiError(e)); setBusy(false); }
  };
  return (
    <div className="p-4 rounded-md border border-amber-200 bg-amber-50 text-sm space-y-2" data-testid="statuts-need-upload">
      <p>Pour générer la nouvelle version, j'ai besoin des <b>statuts actuellement en vigueur</b> (PDF ou Word).</p>
      <Button size="sm" onClick={() => ref.current?.click()} disabled={busy} data-testid="statuts-upload-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Déposer les statuts actuels</Button>
      <input ref={ref} type="file" accept=".pdf,.docx" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
};

// Ouvre soit sur une AGE (assemblyId → génération), soit sur une version existante (versionId)
export const StatutsUpdateDialog = ({ assemblyId, versionId, onClose, onChanged }) => {
  const [v, setV] = useState(null);
  const [state, setState] = useState("idle");
  const open = !!(assemblyId || versionId);

  const generate = async () => {
    setState("loading"); setV(null);
    try { const r = await api.post(`/legal/assemblies/${assemblyId}/statuts/generate`, {}, { timeout: 300000 }); setV(r.data); setState("idle"); onChanged?.(); }
    catch (e) {
      if (e?.response?.status === 409 && /statuts actuels/.test(apiError(e))) { setState("need"); return; }
      setState("idle"); toast.error(apiError(e)); if (e?.response?.status === 409) onClose();
    }
  };
  useEffect(() => {
    if (!open) { setV(null); setState("idle"); return; }
    if (versionId) { api.get(`/legal/statuts/versions/${versionId}`).then((r) => setV(r.data)).catch((e) => toast.error(apiError(e))); }
    else { generate(); }
  }, [assemblyId, versionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (kind) => {
    try {
      if (kind === "validate") {
        if (!window.confirm("Adopter cette version ? Elle deviendra les statuts en vigueur et sera rangée (PDF) dans le coffre-fort.")) return;
        await api.post(`/legal/statuts/versions/${v.id}/validate`); toast.success("Nouveaux statuts adoptés et rangés dans le coffre-fort");
      } else {
        if (!window.confirm("Supprimer ce brouillon de statuts ?")) return;
        await api.delete(`/legal/statuts/versions/${v.id}`); toast.info("Brouillon supprimé");
      }
      onChanged?.(); onClose();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="statuts-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-600" />
            {v ? `Statuts — version ${v.version}` : "Mise à jour des statuts"}
            {v && <Badge variant="outline" className="text-[10px]">{v.status === "draft" ? "Brouillon" : v.status === "validated" ? "En vigueur" : "Archivée"}</Badge>}
          </DialogTitle>
          <DialogDescription>{v ? `${v.label}${v.previous ? ` · comparée à la version ${v.previous.version}` : ""}` : "L'IA réécrit les articles concernés par l'AGE à partir de tes statuts en vigueur."}</DialogDescription>
        </DialogHeader>
        {state === "loading" && <div className="py-12 text-center text-sm text-slate-500" data-testid="statuts-loading"><Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-purple-600" />Rédaction des nouveaux statuts… (jusqu'à 2 minutes)</div>}
        {state === "need" && <NeedStatuts onUploaded={generate} />}
        {v?.stale && <p className="text-sm p-2 rounded bg-red-50 text-red-700 border border-red-200" data-testid="statuts-stale">Brouillon obsolète : une version plus récente des statuts a été adoptée depuis. Régénère-le depuis l'AGE.</p>}
        {v?.diff && v.diff.stats.modifies + v.diff.stats.ajoutes + v.diff.stats.supprimes === 0 && (v.changes || []).length > 0 && (
          <p className="text-sm p-2 rounded bg-amber-50 text-amber-800 border border-amber-200" data-testid="statuts-no-diff">Aucune différence avec la version précédente : ces décisions semblent déjà intégrées.</p>)}
        {v && (
          <Tabs defaultValue="diff">
            <TabsList>
              <TabsTrigger value="diff" data-testid="statuts-tab-diff">Comparaison</TabsTrigger>
              <TabsTrigger value="changes" data-testid="statuts-tab-changes">Changements ({(v.changes || []).length})</TabsTrigger>
              {v.status === "draft" && <TabsTrigger value="edit" data-testid="statuts-tab-edit">Modifier le texte</TabsTrigger>}
            </TabsList>
            <TabsContent value="diff"><StatutsDiffView diff={v.diff} /></TabsContent>
            <TabsContent value="changes"><Changes changes={v.changes} /></TabsContent>
            {v.status === "draft" && <TabsContent value="edit"><Editor v={v} onSaved={setV} /></TabsContent>}
          </Tabs>
        )}
        {v && (
          <div className="flex flex-wrap justify-end gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" onClick={() => downloadFile(v.id, "pdf", v.version)} data-testid="statuts-dl-pdf"><Download className="w-4 h-4 mr-1" />PDF</Button>
            <Button variant="outline" size="sm" onClick={() => downloadFile(v.id, "docx", v.version)} data-testid="statuts-dl-docx"><FileText className="w-4 h-4 mr-1" />Word</Button>
            {v.status === "draft" && <>
              {assemblyId && <Button variant="outline" size="sm" onClick={generate} data-testid="statuts-regen-btn"><Sparkles className="w-4 h-4 mr-1" />Régénérer</Button>}
              <Button variant="ghost" size="sm" className="text-red-600" onClick={() => act("delete")} data-testid="statuts-delete-btn"><Trash2 className="w-4 h-4 mr-1" />Supprimer</Button>
              <Button size="sm" disabled={v.stale} onClick={() => act("validate")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="statuts-validate-btn"><Check className="w-4 h-4 mr-1" />Adopter cette version</Button>
            </>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
